import CompanyTypePage from './CompanyTypePage';
import { useState, useEffect } from 'react';
import { companiesAPI } from '../services/api';
import { getView, setView, hasView } from '../services/viewCache';

const CONFIG = {
  icon: '🌱',
  name: 'Section 8 Company / Trust / NGO',
  shortName: 'Sec 8 / Trust / NGO',
  subtitle: 'Non-profit entities for charitable purposes · Sec 8 Companies Act · Income Tax Sec 12A/80G',
  tags: ['Non-Profit', 'Tax Exemption (12A/80G)', 'FCRA eligible', 'CSR recipient eligible', 'No dividend distribution'],
  heroBg: 'linear-gradient(135deg, #eef4ea, #dfe9d6)',
  heroAccent: '#a7c79b',
  titleColor: '#3a5530',
  subtitleColor: '#4a6b3f',
  tagBg: '#e8efe5',
  tagText: '#3a5530',
  accentColor: '#4a6b3f',
  // Stats will be dynamically loaded
  stats: [],
  filings: [
    { form: 'AOC-4',     title: 'Financial Statements',   deadline: '30d from AGM',   penalty: '₹100/day',         status: 'filed' },
    { form: 'MGT-7',     title: 'Annual Return',           deadline: '60d from AGM',   penalty: '₹100/day',         status: 'filed' },
    { form: 'FC-4',      title: 'FCRA Annual Return',      deadline: '31 Dec yearly',  penalty: 'FCRA cancellation', status: 'pending' },
    { form: 'Form 10B',  title: '12A/80G Audit Report',   deadline: '30 Sep yearly',  penalty: 'Exemption lapse',   status: 'filed' },
    { form: 'CSR-1',     title: 'CSR Registration',        deadline: 'One-time',       penalty: 'CSR funds blocked', status: 'filed' },
  ],
  requirements: [
    { icon: '🎯', title: 'Charitable Objects',   desc: 'Objects must be exclusively charitable/social — no profit distribution to members' },
    { icon: '📋', title: 'Sec 12A / 80G',        desc: 'Apply for income tax exemptions — mandatory for donors to claim deductions' },
    { icon: '🌍', title: 'FCRA Registration',    desc: 'Mandatory for receiving foreign donations — annual FC-4 return by 31 December' },
    { icon: '💰', title: 'CSR Funding',          desc: 'Register on MCA CSR portal (CSR-1) to receive corporate CSR funds' },
    { icon: '📊', title: 'Utilisation Reports',  desc: 'Donors and government require utilisation certificates for grant disbursements' },
    { icon: '🏛️', title: 'AGM Requirements',    desc: 'Annual General Meeting mandatory — same as companies · Accounts audited by CA' },
  ],
  complianceHealth: [
    { label: '12A/80G Filing',    percent: 90, color: '#4a6b3f' },
    { label: 'FCRA Annual Return', percent: 67, color: '#8a6a1f' },
    { label: 'MCA Annual Filing', percent: 88, color: '#4a6b3f' },
  ],
};

const CACHE_KEY = 'companyType:ngo';

export default function NGOPage() {
  // Stale-while-revalidate: seed the last real config so the stats strip does
  // not reset to empty on revisit (services/viewCache).
  const [dynamicConfig, setDynamicConfig] = useState(() => getView(CACHE_KEY) ?? CONFIG);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await companiesAPI.getNGOStats();
        if (response.success && response.data) {
          const stats = response.data;
          const dynamicStats = [
            { label: 'NGOs in Portfolio',     value: stats.totalCompanies.toString(), color: 'text-[#4a6b3f]' },
            { label: 'Fully Compliant',       value: stats.fullyCompliant.toString(), color: 'text-[#4a6b3f]' },
            { label: '12A / 80G Active',      value: stats.overdueFilings.toString(), color: 'text-[#145f5f]' },
            { label: 'FCRA Registered',       value: stats.pendingActions.toString(), color: 'text-[#3a4a66]' },
          ];
          
          const nextConfig = { ...CONFIG, stats: dynamicStats };
          setDynamicConfig(nextConfig);
          setView(CACHE_KEY, nextConfig);
        }
      } catch (error) {
        console.error('Failed to fetch NGO stats:', error);
        // Keep default stats if API fails
        const defaultStats = [
          { label: 'NGOs in Portfolio',     value: '0', color: 'text-[#4a6b3f]' },
          { label: 'Fully Compliant',       value: '0', color: 'text-[#4a6b3f]' },
          { label: '12A / 80G Active',      value: '0', color: 'text-[#145f5f]' },
          { label: 'FCRA Registered',       value: '0', color: 'text-[#3a4a66]' },
        ];
        if (!hasView(CACHE_KEY)) {
          setDynamicConfig(prev => ({
            ...prev,
            stats: defaultStats
          }));
        }
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return <CompanyTypePage config={dynamicConfig} />;
  }

  return <CompanyTypePage config={dynamicConfig} />;
}
